/**
 * TimeseriesChart — line/area chart on a time axis.
 *
 * The primary data-visualization widget. Drives ~60% of dashboards. Supports
 * multiple series, reference ranges (drawn as bands), threshold lines, and
 * the canonical loading / empty / error states.
 *
 * Vertical-agnostic — the surrounding pack widget decides what the series
 * mean. The chart only knows about time, value, and series colour.
 */

import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  Area,
  ComposedChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { Button } from "@/lib/ui/button";
import { dataSpan, formatTime, timeFormats, timeTicks, withGapBreaks } from "./time-axis";
import { ChartSkeleton } from "../ChartSkeleton";
import { seriesColor } from "../series-palette";
import type {
  ThresholdKind,
  TimeseriesSeries,
  WidgetState,
} from "../types";

export interface TimeseriesChartProps {
  /** One or more series to render. */
  series: TimeseriesSeries[];
  /** Render kind. "line" by default; "area" stacks a faint fill under the line. */
  variant?: "line" | "area";
  /** Height in px. Default 280. */
  height?: number;
  /** Show legend below the chart. Default true when series > 1. */
  showLegend?: boolean;
  /** Show the grid. Default true. */
  showGrid?: boolean;
  /** y-axis label. */
  yLabel?: string;
  /**
   * Unit of the value axis (e.g. "°C", "kW", "%"). When all rendered series
   * share one unit, the surrounding widget passes it here so the chart can
   * (a) label the Y axis and (b) suffix the Y-axis ticks with the unit — so a
   * reader can tell what the numbers mean without opening the tooltip. When
   * series carry mixed units the caller omits this (the legend disambiguates).
   */
  yUnit?: string;
  /**
   * Fixed lower bound for the value (Y) axis. When omitted the axis
   * autoranges to fit the data. See {@link yAxisDomain}.
   */
  yMin?: number;
  /**
   * Fixed upper bound for the value (Y) axis. When omitted the axis
   * autoranges to fit the data. See {@link yAxisDomain}.
   */
  yMax?: number;
  /**
   * The stretch of time the chart spans, as epoch milliseconds [from, to]: the x-axis runs over it, so a gap in the
   * data stays a gap, and its ticks read at the span's grain (timeFormats). Default: the data's own first and last
   * points.
   */
  span?: [number, number];
  /** Widget state. Default ok. */
  state?: WidgetState;
  /** Optional override class. */
  className?: string;
}

/**
 * Resolve a Recharts YAxis `domain` from optional fixed bounds.
 *
 *  - both finite  → `[yMin, yMax]` and `allowDataOverflow: true` so the
 *    axis clips to the fixed window instead of expanding to fit outliers;
 *  - only one set → that bound is fixed, the other stays `"auto"`;
 *  - neither set  → `["auto", "auto"]` — explicit autorange (the default;
 *    fits the data with Recharts' usual padding).
 */
export function yAxisDomain(
  yMin?: number,
  yMax?: number,
): { domain: [number | string, number | string]; allowDataOverflow: boolean } {
  const hasMin = typeof yMin === "number" && Number.isFinite(yMin);
  const hasMax = typeof yMax === "number" && Number.isFinite(yMax);
  if (hasMin && hasMax) {
    return { domain: [yMin, yMax], allowDataOverflow: true };
  }
  return {
    domain: [hasMin ? yMin : "auto", hasMax ? yMax : "auto"],
    allowDataOverflow: false,
  };
}

/** The smallest span the value axis shows, as a share of the readings' size (sweep PR-E, D5). */
export const MIN_Y_SPAN_SHARE = 0.05;

/**
 * The value axis of readings that barely move (sweep PR-E, D5: a stuck sensor's last-digit noise filled the chart as
 * a wave). With no bound fixed, readings that span less than 5% of their size get an axis of that 5% around their
 * middle, so they read as the flat line they are. null where they move more, a bound is fixed, they are all zero, or
 * there are none: the axis then fits the data as before. Pure.
 */
export function flatYDomain(
  values: readonly (number | null | undefined)[],
  yMin?: number,
  yMax?: number,
  share: number = MIN_Y_SPAN_SHARE,
): [number, number] | null {
  if ((typeof yMin === "number" && Number.isFinite(yMin)) || (typeof yMax === "number" && Number.isFinite(yMax))) {
    return null;
  }
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo > hi) return null;
  const least = Math.max(Math.abs(lo), Math.abs(hi)) * share;
  if (least === 0 || hi - lo >= least) return null;
  const mid = (lo + hi) / 2;
  return [mid - least / 2, mid + least / 2];
}

const THRESHOLD_COLOR: Record<ThresholdKind, string> = {
  warning: "rgb(var(--color-warning))",
  danger: "rgb(var(--color-danger))",
  info: "rgb(var(--color-info))",
};

export function TimeseriesChart({
  series,
  variant = "line",
  height = 280,
  showLegend,
  showGrid = true,
  yLabel,
  yUnit,
  yMin,
  yMax,
  span,
  state = { kind: "ok" },
  className,
}: TimeseriesChartProps) {
  const rows = useMemo(() => seriesRows(series), [series]);
  const [spanFrom, spanTo] = useMemo(() => span ?? dataSpan(rows.flat()), [span, rows]);
  const formats = timeFormats(spanTo - spanFrom);
  // Round times over the whole span: left to itself, Recharts ticks a time axis at the readings' own times only.
  const ticks = useMemo(() => timeTicks(spanFrom, spanTo), [spanFrom, spanTo]);
  // A reading with no neighbour on either side (between two gaps) draws no line: it gets a dot.
  const lone = useMemo(() => rows.map((r, i) => loneRows(r, `series_${series[i]?.id}`)), [rows, series]);
  // Readings that barely move keep a minimum span, so a stuck sensor reads flat (flatYDomain); else the fixed bounds or
  // the data's own fit.
  const flat = useMemo(() => flatYDomain(series.flatMap((s) => s.points.map((p) => p.v)), yMin, yMax), [series, yMin, yMax]);
  const yAxis = flat ? { domain: flat, allowDataOverflow: false } : yAxisDomain(yMin, yMax);
  const hasData = rows.some((r) => r.length > 0);
  // Value-axis label: an explicit `yLabel` wins; otherwise fall back to the
  // unit so the axis is never unitless when the unit is known (#105). A single
  // rotated label reads cleaner than suffixing every tick in a small tile.
  const effectiveYLabel = yLabel ?? yUnit;

  if (state.kind === "loading") {
    return <ChartSkeleton shape={variant === "area" ? "area" : "line"} count={series.length} height={height} className={className} />;
  }

  if (state.kind === "error") {
    return (
      <div
        className={cn(
          "flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-danger/40 bg-danger/5 p-6 text-center",
          className,
        )}
        style={{ height }}
      >
        <AlertTriangle className="size-8 text-danger" />
        <p className="text-sm text-on-surface">{state.message}</p>
        {state.retry && (
          <Button size="sm" variant="outline" onClick={state.retry}>
            <RefreshCw className="mr-1.5 size-3.5" />
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (state.kind === "empty" || !hasData) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center rounded-md border border-dashed border-border bg-surface-elevated/40 text-sm text-on-surface-muted",
          className,
        )}
        style={{ height }}
      >
        No data in the selected time range.
      </div>
    );
  }

  const effectiveLegend = showLegend ?? series.length > 1;
  const allThresholds = series.flatMap((s, idx) =>
    (s.thresholds ?? []).map((t) => ({ ...t, seriesIdx: idx })),
  );
  const allRefs = series
    .map((s, idx) =>
      s.referenceRange
        ? { ...s.referenceRange, seriesIdx: idx, label: s.label }
        : null,
    )
    .filter((r): r is { min: number; max: number; seriesIdx: number; label: string } => r !== null);

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart margin={{ top: 8, right: 12, bottom: 8, left: 8 }}>
          {showGrid && (
            <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
          )}
          {/* Each series has its own rows, so a hover finds a series' reading by its time: with duplicated categories
              allowed (the default) Recharts reads it by position among all the series' rows, another point's. */}
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            allowDuplicatedCategory={false}
            domain={[spanFrom, spanTo]}
            ticks={ticks}
            tickFormatter={(v: number) => formatTime(v, formats.tick)}
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            tickMargin={6}
            minTickGap={32}
          />
          <YAxis
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            tickMargin={4}
            width={48}
            domain={yAxis.domain}
            allowDataOverflow={yAxis.allowDataOverflow}
            label={
              effectiveYLabel
                ? {
                    value: effectiveYLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      textAnchor: "middle",
                      fontSize: 11,
                      fill: "rgb(var(--color-on-surface-muted))",
                    },
                  }
                : undefined
            }
          />
          <Tooltip content={<ChartTooltip labelFormat={formats.tooltip} />} />
          {effectiveLegend && (
            <Legend
              verticalAlign="bottom"
              height={28}
              iconType="line"
              wrapperStyle={{ fontSize: 11 }}
            />
          )}

          {allRefs.map((ref, i) => (
            <ReferenceArea
              key={`ref-${i}`}
              y1={ref.min}
              y2={ref.max}
              fill={seriesColor(ref.seriesIdx, series[ref.seriesIdx]?.color)}
              fillOpacity={0.05}
              stroke="none"
            />
          ))}

          {allThresholds.map((th, i) => (
            <ReferenceLine
              key={`th-${i}`}
              y={th.value}
              stroke={THRESHOLD_COLOR[th.kind]}
              strokeDasharray="4 3"
              strokeWidth={1.2}
              label={{
                value: th.label,
                position: "right",
                style: {
                  fontSize: 10,
                  fill: THRESHOLD_COLOR[th.kind],
                },
              }}
            />
          ))}

          {/* Each line draws from its own rows, so another series' timestamps never put gaps in it. */}
          {series.map((s, idx) => {
            const color = seriesColor(idx, s.color);
            const dot = lone[idx].size > 0 ? loneDot(lone[idx], color) : false;
            if (variant === "area") {
              return (
                <Area
                  key={s.id}
                  data={rows[idx]}
                  type="monotone"
                  dataKey={`series_${s.id}`}
                  name={s.label}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.12}
                  strokeWidth={1.8}
                  isAnimationActive={false}
                  dot={dot}
                  connectNulls={false}
                />
              );
            }
            return (
              <Line
                key={s.id}
                data={rows[idx]}
                type="monotone"
                dataKey={`series_${s.id}`}
                name={s.label}
                stroke={color}
                strokeWidth={1.8}
                isAnimationActive={false}
                dot={dot}
                connectNulls={false}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Each series' own rows, in time order: { ts (epoch ms, the x-axis value), series_<id>: value | null }. A point whose
 * time does not parse is left out. Series are never merged onto one row set: two tags on different time bases (15
 * minute buckets beside raw change readings) would put a null in each at every one of the other's times, and the
 * lines would break into dots. A point whose value is null stays null, so a real gap stays a gap; a bucketed series
 * also gets a null row in every stretch of more than two buckets with no point (withGapBreaks).
 */
function seriesRows(series: TimeseriesSeries[]): Array<Array<{ ts: number } & Record<string, number | null>>> {
  return series.map((s) => {
    const key = `series_${s.id}`;
    const rows: Array<{ ts: number } & Record<string, number | null>> = [];
    for (const p of s.points) {
      const ts = Date.parse(p.t);
      if (Number.isFinite(ts)) rows.push({ ts, [key]: p.v });
    }
    rows.sort((a, b) => a.ts - b.ts);
    return withGapBreaks(rows, s.bucketMs, (ts) => ({ ts, [key]: null }));
  });
}

/** The indexes of a series' rows that hold a reading with no reading beside it on either side. */
function loneRows(rows: ReadonlyArray<Record<string, number | null>>, key: string): Set<number> {
  const has = (i: number) => i >= 0 && i < rows.length && rows[i][key] != null;
  const out = new Set<number>();
  if (rows.length < 2) return out;
  for (let i = 0; i < rows.length; i++) if (has(i) && !has(i - 1) && !has(i + 1)) out.add(i);
  return out;
}

/** A line's dots: one at each lone reading only, so a reading between two gaps still shows. */
function loneDot(lone: Set<number>, color: string) {
  function LoneDot(p: { cx?: number; cy?: number; index?: number }) {
    if (p.index === undefined || !lone.has(p.index) || p.cx == null || p.cy == null) return <g />;
    return <circle cx={p.cx} cy={p.cy} r={2.5} fill={color} stroke="none" />;
  }
  return LoneDot;
}

interface TooltipPayloadEntry {
  name: string;
  value: number | string | null;
  color: string;
  dataKey: string;
}

function ChartTooltip(props: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: number | string;
  labelFormat?: string;
}) {
  const { active, label, labelFormat = "d MMM yyyy, HH:mm" } = props;
  // A gap's null row holds no reading: it is not shown.
  const payload = props.payload?.filter((e) => e.value != null);
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-surface-elevated p-2.5 text-xs shadow-md">
      <p className="mb-1 font-medium text-on-surface">
        {label != null && label !== "" ? formatTime(Number(label), labelFormat) : ""}
      </p>
      <ul className="flex flex-col gap-0.5">
        {payload.map((entry) => (
          <li key={entry.dataKey} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-on-surface-muted">
              <span
                className="inline-block size-2 rounded-full"
                style={{ backgroundColor: entry.color }}
                aria-hidden
              />
              {entry.name}
            </span>
            <span className="font-mono text-on-surface">
              {typeof entry.value === "number"
                ? entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : entry.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
